import React, { useMemo } from 'react'
import SimpleComboBox from '@/components/SimpleComboBox'
import useGetIncomeCategories from '@/services/api/transaction-category/useGetIncomeCategories'
import useGetExpenseCategories from '@/services/api/transaction-category/useGetExpenseCategories'
import type { Direction } from '@/services/api/integrations/useXeroMapping'

export interface MappableCategory {
  id: number
  name: string
  group?: string
}

/**
 * Active categories for one direction, flattened out of their groups. The
 * The "Unreconciled" system categories are the fallback, not a mapping target.
 */
export function useMappableCategories(direction: Direction) {
  const income = useGetIncomeCategories()
  const expense = useGetExpenseCategories()
  const data = direction === 'in' ? income.data : expense.data

  return useMemo<MappableCategory[]>(() => {
    const list: MappableCategory[] = []
    for (const item of data ?? []) {
      if (item.is_group) {
        if (item.id === -1) continue // "Archived" pseudo-group
        for (const category of item.transaction_categories ?? []) {
          if (!category.system_key) {
            list.push({
              id: category.id,
              name: category.name,
              group: item.name,
            })
          }
        }
      } else if (!item.system_key && !item.is_archived) {
        list.push({ id: item.id, name: item.name })
      }
    }
    return list
  }, [data])
}

// Search matches the item's value, so the value carries the name (ids stay unique).
const optionValue = (category: MappableCategory) =>
  `${category.name} ${category.group ?? ''} #${category.id}`

export default function CategoryPicker({
  direction,
  value,
  onChange,
  disabled,
  placeholder = 'Choose a category',
}: {
  direction: Direction
  value: number | null
  onChange: (categoryId: number | null) => void
  disabled?: boolean
  placeholder?: string
}) {
  const categories = useMappableCategories(direction)
  const selected = categories.find((c) => c.id === value) ?? null

  return (
    <SimpleComboBox
      items={categories}
      selected={selected}
      setSelected={(category: MappableCategory | null) =>
        onChange(category?.id ?? null)
      }
      getValue={optionValue}
      getOptionLabel={(category: MappableCategory) => (
        <span>
          {category.name}
          {category.group && (
            <span data-slot="description" className="ml-1 text-xs">
              · {category.group}
            </span>
          )}
        </span>
      )}
      getSelectedLabel={(category: MappableCategory) => category.name}
      placeholder={placeholder}
      disabled={disabled}
      empty={`No ${direction === 'in' ? 'Money In' : 'Money Out'} categories`}
    />
  )
}
